import {createContext, useContext, useEffect, useReducer} from 'react'

import {KilnConfigActionType, type AssetConfigurationDto} from '../../types'

import {getEmptyKilnConfig, getKilnConfig, kilnConfigReducer} from './helpers'
import type {KilnConfigContextState} from './types'

export const KilnConfigContext = createContext<KilnConfigContextState | undefined>(undefined)

export const useKilnConfig = () => {
  const context = useContext(KilnConfigContext)
  if (context === undefined) {
    throw new Error('useKilnConfig must be used within a KilnConfigProvider')
  }
  return context
}

interface KilnConfigProviderProps {
  assetConfig?: AssetConfigurationDto
  children: React.ReactNode
}

export const KilnConfigProvider: React.FC<KilnConfigProviderProps> = ({assetConfig, children}) => {
  const initialKilnConfig = assetConfig ? getKilnConfig(assetConfig) : getEmptyKilnConfig()

  const [kilnConfig, dispatch] = useReducer(kilnConfigReducer, initialKilnConfig)

  // Selected signals change in many reducer actions so useEffect guarantees we catch all changes
  useEffect(() => {
    dispatch({type: KilnConfigActionType.VERIFY_HEALTH_INDICATORS})
  }, [kilnConfig.layout.selectedSignals])

  return (
    <KilnConfigContext.Provider
      value={{
        kilnLayout: kilnConfig.layout,
        kilnConfiguration: kilnConfig.configuration,
        kilnSignalMappings: kilnConfig.signalMappings,
        dispatch
      }}
    >
      {children}
    </KilnConfigContext.Provider>
  )
}
